import {useAnalytics} from '@github-ui/use-analytics'
import {Banner} from '@primer/react'
import {useCallback, useEffect, useState} from 'react'

import styles from './StaleCacheBanner.module.css'

/**
 * Shows a warning banner when the Relay cache serves stale data (the `stale-cache-warning`
 * window event dispatched by `@github-ui/relay-query-cache`), offering a reload. Shared by the
 * issues app and the issues/pulls dashboards so they surface the same recovery affordance.
 */
export function StaleCacheBanner() {
  const [bannerVisible, setBannerVisible] = useState(false)
  const {sendAnalyticsEvent} = useAnalytics()

  const handleStaleCacheWarning = useCallback(
    (event: Event) => {
      setBannerVisible(true)
      const customEvent = event as CustomEvent<{errors: unknown[]}>
      const errors = customEvent.detail?.errors || []
      sendAnalyticsEvent('issues.stale_cache_warning.shown', undefined, {
        errorCount: errors.length,
        errors: JSON.stringify(errors),
      })
    },
    [sendAnalyticsEvent],
  )

  useEffect(() => {
    window.addEventListener('stale-cache-warning', handleStaleCacheWarning)
    return () => {
      window.removeEventListener('stale-cache-warning', handleStaleCacheWarning)
    }
  }, [handleStaleCacheWarning])

  if (!bannerVisible) return null

  const handleReload = () => {
    window.location.reload()
  }

  return (
    <div className={styles.container}>
      <Banner
        variant="warning"
        title="Stale cache warning"
        hideTitle
        description="Failed to fetch data."
        primaryAction={
          <Banner.PrimaryAction type="button" onClick={handleReload}>
            Reload the page
          </Banner.PrimaryAction>
        }
        aria-live="polite"
        aria-atomic="true"
      />
    </div>
  )
}
